import type React from 'react';
import type { CSSProperties } from 'react';
import { useMemo, useState, Children, useRef, useEffect, useCallback } from 'react';

interface IViewportProps {
    prefix?: string;
    marginBottom?: string;
    withScroll?: boolean;
    onScroll?: React.UIEventHandler<HTMLDivElement>;
    shownChildIndex?: number;
    scrollTopOnIndexChange?: boolean;
    scrollTopValue?: number;
    className?: string;
    topIndention?: string;
}

export const Viewport: React.FC<IViewportProps> = (props) => {
    const {
        children,
        prefix = '--viewport',
        marginBottom = '',
        withScroll = true,
        onScroll,
        shownChildIndex,
        scrollTopOnIndexChange,
        scrollTopValue = 0,
        className,
        topIndention = '0px',
    } = props;
    const [cssVariables, setCssVariables] = useState<React.CSSProperties>({});
    const showOneChild = Number.isFinite(shownChildIndex);

    const measuredRef = useCallback(
        (node) => {
            if (node !== null) {
                setCssVariables(
                    Object.entries(node.getBoundingClientRect?.().toJSON() ?? {}).reduce(
                        (acc: Record<string, string>, [key, value]) => {
                            acc[`${prefix}-${key}`] = `${value}px`;

                            return acc;
                        },
                        {},
                    ),
                );
            }
        },
        [prefix],
    );

    const ref = useRef<HTMLDivElement>(null);

    const cssScroll = useMemo(
        () =>
            withScroll
                ? {
                      height: `calc(100vh - ${topIndention} - var(${prefix}-top)${
                          marginBottom && ` - ${marginBottom}`
                      } - 50px)`,
                      width: '100%',
                      overflow: 'auto',
                  }
                : undefined,
        [topIndention, marginBottom, prefix, withScroll],
    );

    useEffect(() => {
        if (scrollTopOnIndexChange && ref.current?.parentElement) {
            ref.current.parentElement.scrollTo({
                top: ref.current.parentElement.scrollTop === scrollTopValue ? scrollTopValue + 1 : scrollTopValue,
                behavior: 'smooth',
            });
        }
    }, [scrollTopOnIndexChange, scrollTopValue, shownChildIndex]);

    return (
        <div
            className="viewport"
            ref={measuredRef}
            style={{ ...cssVariables, ...cssScroll } as CSSProperties}
            onScroll={onScroll}
        >
            <div ref={ref} className={className} style={{ height: '100%' }}>
                {showOneChild &&
                    Children.toArray(children).map((child, index) => (
                        <div
                            key={index}
                            style={
                                shownChildIndex !== index
                                    ? {
                                          visibility: 'hidden',
                                          position: 'absolute',
                                          overflow: 'hidden',
                                          maxHeight: 0,
                                      }
                                    : { height: '100%' }
                            }
                        >
                            {child}
                        </div>
                    ))}
                {!showOneChild && children}
            </div>
        </div>
    );
};

Viewport.displayName = 'Viewport';
